import { QueryClient, useQuery } from "@tanstack/react-query";
import axios, { AxiosError } from "axios";

import { UseGetHealthResponse, HealthResponse } from "types/queries";

export const healthKeys = {
  all: [{ scope: "health" }] as const,
  health: () =>
    [
      {
        scope: "health",
        data: "healthCheck",
      },
    ] as const,
};

/**
 * Set defaults for product queries
 *
 * Setting these here applies them globally whether called directly or via custom hooks
 *
 * Also helpful for testing as they can be easily overriden
 * (e.g. to stop retries from slowing tests down)
 */
export function setHealthQueryDefaults(queryClient: QueryClient) {
  queryClient.setQueryDefaults([{ data: "healthCheck" }], {
    staleTime: Infinity,
    cacheTime: Infinity,
    keepPreviousData: true,
    refetchInterval(data: UseGetHealthResponse | null) {
      // If the health check fails, retry every 5s. Otherwise wait 30s between checks.
      return data?.status === 200 ? 30000 : 5000;
    },
  });
}

/************************************************************************************************
 * QUERIES
 *
 * For each query, we define a hook and a fetcher function.
 *
 * Each hook wraps `useQuery` with configuration on when to fetch and parameters.
 *
 * The fetcher is separate from the hook for use outside of React components (e.g. on server).
 ************************************************************************************************/

/**
 * Fetch health check from local API route
 *
 * Even if it `503`s, consider it a successful response
 */
export async function getHealth() {
  return axios
    .get<HealthResponse>("/_canvas/api/health")
    .then((response) => response.data)
    .catch((error) => error.response?.data);
}

/**
 * Return the health status
 */
export function useGetHealth() {
  return useQuery<UseGetHealthResponse, AxiosError<UseGetHealthResponse>>(
    healthKeys.health(),
    getHealth
  );
}
